import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonicModule } from '@ionic/angular';
import { CalendarModule } from '@syncfusion/ej2-angular-calendars';
import { LinkEncryptionHelper } from '../../helpers/link-encryption.helper';
import { SupportAppointment, SupportBookingService, SupportSlot } from '../../services/support-booking.service';

interface AppointmentLinkTokenPayload {
	s: number;
	n: string;
	c: number[];
	d: number;
	e: number | null;
	li: string | null;
	st: string | null;
}

interface AppointmentLinkPayload {
	salonId: number;
	salonName: string;
	collaborators: number[];
	duration: number;
	expires: string | null;
	licenseId: string | null;
	state: string | null;
}

type PageState = 'invalid' | 'expired' | 'ready';

@Component({
	selector: 'app-appointment-booking-landing',
	templateUrl: './appointment-booking-landing.component.html',
	styleUrls: ['./appointment-booking-landing.component.scss'],
	standalone: true,
	imports: [CommonModule, FormsModule, IonicModule, CalendarModule],
})
export class AppointmentBookingLandingComponent implements OnInit {
	state: PageState = 'invalid';
	payload: AppointmentLinkPayload | null = null;

	selectedDate: Date = new Date();
	minDate: Date = new Date();
	maxDate: Date | null = null;

	slots: SupportSlot[] = [];
	loadingSlots = false;
	selectedSlot: SupportSlot | null = null;

	previousAppointments: SupportAppointment[] = [];
	loadingAppointments = false;
	historyExpanded = false;

	confirming = false;
	bookingSuccess = false;

	note = '';
	editingAppointment: SupportAppointment | null = null;
	deletingAppointmentId: number | null = null;

	constructor(
		private route: ActivatedRoute,
		private supportBookingService: SupportBookingService,
		private alertCtrl: AlertController,
	) {}

	ngOnInit() {
		const token = this.route.snapshot.queryParamMap.get('data');

		if (!token) {
			this.state = 'invalid';
			return;
		}

		const decoded = LinkEncryptionHelper.decrypt<AppointmentLinkTokenPayload>(token);

		if (!decoded || !decoded.s) {
			this.state = 'invalid';
			return;
		}

		const expires = decoded.e ? LinkEncryptionHelper.julianDayToDate(decoded.e) : null;
		if (expires) {
			expires.setHours(23, 59, 59, 999);
		}

		if (expires && expires.getTime() < Date.now()) {
			this.state = 'expired';
			return;
		}

		this.payload = {
			salonId: decoded.s,
			salonName: decoded.n,
			collaborators: decoded.c || [],
			duration: decoded.d,
			expires: expires ? expires.toISOString() : null,
			licenseId: decoded.li || null,
			state: decoded.st || null,
		};
		this.state = 'ready';

		this.minDate = new Date();
		this.maxDate = expires;
		this.selectedDate = new Date();

		this.loadSlots();
		this.loadAppointments();
	}

	get expirationDate(): Date | null {
		return this.payload?.expires ? new Date(this.payload.expires) : null;
	}

	get collaboratorsCount(): number {
		return this.payload?.collaborators?.length ?? 0;
	}

	get bookableSlots(): SupportSlot[] {
		return this.slots.filter((s) => s.IsBookable);
	}

	onDateChange(date: Date) {
		if (!date) return;
		this.selectedDate = date;
		this.selectedSlot = null;
		this.loadSlots();
	}

	selectSlot(slot: SupportSlot) {
		if (!slot.IsBookable) return;
		this.selectedSlot = slot;
	}

	toggleHistory() {
		this.historyExpanded = !this.historyExpanded;
	}

	isAppointmentExpired(appointment: SupportAppointment): boolean {
		const date = appointment?.Support?.SupportDate;
		if (!date) return true;
		return new Date(date).getTime() < Date.now();
	}

	editAppointment(appointment: SupportAppointment, event: Event) {
		event.stopPropagation();
		if (this.isAppointmentExpired(appointment)) return;

		this.editingAppointment = appointment;

		const date = appointment.Support.SupportDate;
		if (date) {
			this.onDateChange(new Date(date));
		}

		document.querySelector('.booking-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	cancelEdit() {
		this.editingAppointment = null;
		this.selectedSlot = null;
	}

	async deleteAppointment(appointment: SupportAppointment, event: Event) {
		event.stopPropagation();
		if (this.isAppointmentExpired(appointment) || !this.payload) return;

		const alert = await this.alertCtrl.create({
			header: 'Elimina appuntamento',
			message: `Confermi l'eliminazione dell'appuntamento del ${new Date(appointment.Support.SupportDate).toLocaleDateString('it-IT')} alle ${new Date(appointment.Support.SupportDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}?`,
			buttons: [
				{ text: 'Annulla', role: 'cancel' },
				{
					text: 'Elimina',
					role: 'destructive',
					handler: () => this.submitDeleteAppointment(appointment),
				},
			],
		});

		await alert.present();
	}

	private submitDeleteAppointment(appointment: SupportAppointment) {
		if (!this.payload) return;

		this.deletingAppointmentId = appointment.Support.Id;

		this.supportBookingService.deletePublicAppointment(appointment.Support.Id, this.payload.salonId).subscribe({
			next: () => {
				this.deletingAppointmentId = null;

				if (this.editingAppointment?.Support?.Id === appointment.Support.Id) {
					this.cancelEdit();
				}

				this.loadAppointments();
				this.loadSlots();
			},
			error: async () => {
				this.deletingAppointmentId = null;

				const alert = await this.alertCtrl.create({
					header: 'Impossibile eliminare',
					message: "Si è verificato un errore durante l'eliminazione dell'appuntamento, riprova più tardi.",
					buttons: ['OK'],
				});
				await alert.present();
			},
		});
	}

	get editingTimeString(): string | null {
		const date = this.editingAppointment?.Support?.SupportDate;
		if (!date) return null;
		return new Date(date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
	}

	get editingDateString(): string | null {
		const date = this.editingAppointment?.Support?.SupportDate;
		if (!date) return null;
		return new Date(date).toLocaleDateString('it-IT');
	}

	async confirmAppointment() {
		if (!this.selectedSlot || !this.payload || this.confirming) return;

		const alert = await this.alertCtrl.create({
			header: 'Conferma richiesta',
			message: `Confermi la richiesta di appuntamento per il ${this.selectedDate.toLocaleDateString('it-IT')} alle ${this.selectedSlot.TimeString}?`,
			buttons: [
				{ text: 'Annulla', role: 'cancel' },
				{
					text: 'Conferma',
					handler: () => this.submitAppointment(),
				},
			],
		});

		await alert.present();
	}

	private submitAppointment() {
		if (!this.selectedSlot || !this.payload) return;

		this.confirming = true;

		this.supportBookingService
			.createPublicAppointment(
				this.payload.salonId,
				this.selectedDate,
				this.selectedSlot.TimeString,
				this.payload.duration,
				this.payload.collaborators,
				this.payload.licenseId,
				this.note,
				this.payload.state,
			)
			.subscribe({
				next: async () => {
					this.confirming = false;
					this.bookingSuccess = true;
					this.selectedSlot = null;
					this.editingAppointment = null;
					this.note = '';
					this.loadSlots();
					this.loadAppointments();

					const alert = await this.alertCtrl.create({
						header: 'Richiesta inviata',
						message: 'Il tuo appuntamento è stato registrato con successo. Il Supporto SalonApp ti contatterà a breve.',
						buttons: ['OK'],
					});
					await alert.present();
				},
				error: async (err) => {
					this.confirming = false;

					const message = err?.error?.message || 'Si è verificato un errore, riprova più tardi.';

					const alert = await this.alertCtrl.create({
						header: 'Impossibile confermare',
						message,
						buttons: ['OK'],
					});
					await alert.present();

					this.loadSlots();
				},
			});
	}

	private loadAppointments() {
		if (!this.payload) return;

		this.loadingAppointments = true;

		this.supportBookingService
			.getPublicClientAppointments(this.payload.salonId)
			.subscribe({
				next: (appointments) => {
					this.previousAppointments = appointments || [];
					this.loadingAppointments = false;
				},
				error: () => {
					this.previousAppointments = [];
					this.loadingAppointments = false;
				},
			});
	}

	private loadSlots() {
		if (!this.payload) return;

		this.loadingSlots = true;
		this.slots = [];

		this.supportBookingService
			.getPublicAvailableSlots(
				this.selectedDate,
				this.payload.duration,
				this.payload.collaborators,
			)
			.subscribe({
				next: (slots) => {
					this.slots = slots || [];
					this.loadingSlots = false;
					this.tryPreselectEditingSlot();
				},
				error: () => {
					this.slots = [];
					this.loadingSlots = false;
				},
			});
	}

	private tryPreselectEditingSlot() {
		if (!this.editingAppointment) return;

		const editDate = new Date(this.editingAppointment.Support.SupportDate);
		if (editDate.toDateString() !== this.selectedDate.toDateString()) return;

		const timeString = editDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
		const match = this.slots.find((s) => s.TimeString === timeString);
		if (match) {
			this.selectedSlot = match;
		}
	}
}
